import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CompanyProvider } from "./contexts/CompanyContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import VerifyEmail from "./pages/VerifyEmail";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Guide from "./pages/Guide";
import DataProcessingAgreement from "./pages/DataProcessingAgreement";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import TimeRecords from "./pages/TimeRecords";
import Shifts from "./pages/Shifts";
import Planning from "./pages/Planning";
import Calendars from "./pages/Calendars";
import Vacations from "./pages/Vacations";
import Absences from "./pages/Absences";
import Locations from "./pages/Locations";
import ExportTemplates from "./pages/ExportTemplates";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import Kiosk from "./pages/Kiosk";
import MyShifts from "./pages/MyShifts";
import MiJornada from "./pages/MiJornada";
import Profile from "./pages/Profile";
import Organizations from "./pages/Organizations";
import Incidents from "./pages/Incidents";
import Notifications from "./pages/Notifications";
import Superadmins from "./pages/Superadmins";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyRecord from "./pages/VerifyRecord";
import QrDisplay from "./pages/QrDisplay";
import QrScanner from "./pages/QrScanner";
import QrClock from "./pages/QrClock";

// Components
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8 border-indigo-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Superadmin can access everything
  if (user.role === 'superadmin') {
    return <Layout>{children}</Layout>;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8 border-indigo-600" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/guide" element={<Guide />} />
      <Route path="/dpa" element={<DataProcessingAgreement />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify/:hash" element={<VerifyRecord />} />
      <Route path="/verify" element={<VerifyRecord />} />
      <Route path="/kiosk/:orgSlug" element={<Kiosk />} />
      <Route path="/qr-display" element={<QrDisplay />} />
      <Route path="/qr-scanner" element={<QrScanner />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/employees" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor', 'inspector', 'gestor']}>
          <Employees />
        </ProtectedRoute>
      } />
      
      <Route path="/time-records" element={
        <ProtectedRoute>
          <TimeRecords />
        </ProtectedRoute>
      } />
      
      <Route path="/shifts" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor', 'inspector']}>
          <Shifts />
        </ProtectedRoute>
      } />
      
      <Route path="/planning" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor', 'inspector']}>
          <Planning />
        </ProtectedRoute>
      } />
      
      <Route path="/calendars" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor', 'inspector']}>
          <Calendars />
        </ProtectedRoute>
      } />
      
      <Route path="/vacations" element={
        <ProtectedRoute>
          <Vacations />
        </ProtectedRoute>
      } />
      
      <Route path="/absences" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor', 'inspector']}>
          <Absences />
        </ProtectedRoute>
      } />
      
      <Route path="/locations" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Locations />
        </ProtectedRoute>
      } />

      <Route path="/export-templates" element={
        <ProtectedRoute allowedRoles={['admin', 'gestor']}>
          <ExportTemplates />
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor', 'worker', 'inspector', 'gestor']}>
          <Reports />
        </ProtectedRoute>
      } />
      
      <Route path="/my-shifts" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <MyShifts />
        </ProtectedRoute>
      } />

      <Route path="/mi-jornada" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <MiJornada />
        </ProtectedRoute>
      } />

      <Route path="/qr-clock" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <QrClock />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor', 'worker', 'inspector']}>
          <Profile />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Settings />
        </ProtectedRoute>
      } />

      <Route path="/settings/billing" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Billing />
        </ProtectedRoute>
      } />

      <Route path="/organizations" element={
        <ProtectedRoute allowedRoles={['superadmin']}>
          <Organizations />
        </ProtectedRoute>
      } />
      
      <Route path="/superadmins" element={
        <ProtectedRoute allowedRoles={['superadmin']}>
          <Superadmins />
        </ProtectedRoute>
      } />
      
      <Route path="/incidents" element={
        <ProtectedRoute>
          <Incidents />
        </ProtectedRoute>
      } />
      
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <CompanyProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-right" />
            </BrowserRouter>
          </CompanyProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
